// -------------------------------
// TICKET INTERFACES
// -------------------------------
export interface PendingTicket {
    title?: string;
    categoryId?: number;
    departmentId?: number;
    assignTo?: number;
    description?: string;
    priority?: string; // 0 | 1 | 2
    status?: string;   // Open | Pending | Closed
    ticketDate?: string;
    document?: File | null;
}

export interface PendingDeleteTicket {
    awaitingTitle?: boolean;
}

export const startCreateTicketFlow = (
    setPending: (data: PendingTicket | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    titleFromIntent?: string
) => {
    if (titleFromIntent) {
        setPending({ title: titleFromIntent });
        pushAIChat(`Got it. Which category is this ticket for?`);
        speak(`Which category is this ticket for?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What is the ticket subject?");
    speak("What is the ticket subject?");
};

export const processPendingTicket = (
    text: string,
    pending: PendingTicket,
    setPending: (data: PendingTicket | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    helpers: {
        getCategoryIdByName: (name: string) => number | null;
        getDepartmentIdByName: (name: string) => number | null;
        getEmployeeIdByName: (name: string) => number | null;
    }
) => {

    // STEP 1 — TITLE
    if (!pending.title) {
        const title = text.trim();
        if (!title) {
            pushAIChat("Please provide a valid ticket subject.");
            speak("Please provide a valid ticket subject.");
            return;
        }

        setPending({ ...pending, title });
        pushAIChat("Which category does this ticket belong to?");
        speak("Which category does this ticket belong to?");
        return;
    }

    // STEP 2 — CATEGORY
    if (!pending.categoryId) {
        const categoryId = helpers.getCategoryIdByName(text);
        if (!categoryId) {
            pushAIChat("I couldn't find that category. Please try again.");
            speak("Category not found.");
            return;
        }

        setPending({ ...pending, categoryId });
        pushAIChat("Which department is this ticket for?");
        speak("Which department is this ticket for?");
        return;
    }

    // STEP 3 — DEPARTMENT
    if (!pending.departmentId) {
        const departmentId = helpers.getDepartmentIdByName(text);
        if (!departmentId) {
            pushAIChat("Department not found. Please say it again.");
            speak("Department not found.");
            return;
        }

        setPending({ ...pending, departmentId });
        pushAIChat("Who should this be assigned to?");
        speak("Who should this be assigned to?");
        return;
    }

    // STEP 4 — ASSIGN TO
    if (!pending.assignTo) {
        const assignTo = helpers.getEmployeeIdByName(text);
        if (!assignTo) {
            pushAIChat("Employee not found. Please try again.");
            speak("Employee not found.");
            return;
        }

        setPending({ ...pending, assignTo });
        pushAIChat("Please describe the issue.");
        speak("Please describe the issue.");
        return;
    }

    // STEP 5 — DESCRIPTION
    if (!pending.description) {
        setPending({ ...pending, description: text });
        pushAIChat("What is the priority? High, Medium, or Low?");
        speak("What is the priority?");
        return;
    }

    // STEP 6 — PRIORITY
    if (!pending.priority) {
        const priority =
            text.toLowerCase().includes("high") ? "0" :
            text.toLowerCase().includes("medium") ? "1" : "2";

        setPending({
            ...pending,
            priority,
            status: "Open",
            ticketDate: new Date().toISOString().split("T")[0]
        });

        pushAIChat(`Creating ticket "${pending.title}"...`);
        speak(`Creating ticket ${pending.title}`);

        window.dispatchEvent(
            new CustomEvent("ai-create-ticket", {
                detail: {
                    title: pending.title,
                    categoryId: pending.categoryId,
                    departmentId: pending.departmentId,
                    assignTo: pending.assignTo,
                    description: pending.description,
                    priority,
                    status: "Open",
                    ticketDate: new Date().toISOString().split("T")[0],
                    document: null
                }
            })
        );

        setPending(null);
    }
};

export const startDeleteTicketFlow = (
    setPending: (data: PendingDeleteTicket | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    titleFromIntent?: string
) => {
    if (titleFromIntent) {
        pushAIChat(`Deleting ticket "${titleFromIntent}"...`);
        speak(`Deleting ticket ${titleFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-ticket", {
                detail: { title: titleFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingTitle: true });
    pushAIChat("Which ticket would you like to delete?");
    speak("Which ticket would you like to delete?");
};

export const processPendingDeleteTicket = (
    text: string,
    pending: PendingDeleteTicket,
    setPending: (data: PendingDeleteTicket | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingTitle) return;

    const title = text.trim();
    if (!title) {
        pushAIChat("Please provide a valid ticket subject.");
        speak("Please provide a valid ticket subject.");
        return;
    }

    pushAIChat(`Deleting ticket "${title}"...`);
    speak(`Deleting ticket ${title}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-ticket", {
            detail: { title }
        })
    );

    setPending(null);
};

